import { CircularProgress } from "@mui/material";
import { useEffect, useRef } from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import AlertBlock from "@/components/molecules/AlertBlock";
import useGetOrderPdf from "@/hooks/requests/orders/use-get-order-pdf";
import { OrderDetailsMessages } from "@/messages/order-details/order-details.messages";

type Props = {
  messages: OrderDetailsMessages;
  orderId: number;
};

function OrderPdfPrint({ messages, orderId }: Props) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const hasPrintedRef = useRef(false);
  const { pdfUrl, isLoading, error } = useGetOrderPdf(orderId);

  const printPdf = () => {
    iframeRef.current?.contentWindow?.focus();
    iframeRef.current?.contentWindow?.print();
  };

  useEffect(() => {
    hasPrintedRef.current = false;
  }, [pdfUrl]);

  const handleIframeLoad = () => {
    if (!pdfUrl || hasPrintedRef.current) return;

    hasPrintedRef.current = true;
    window.setTimeout(printPdf, 400);
  };

  if (isLoading) {
    return (
      <MDBox display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </MDBox>
    );
  }

  if (error || !pdfUrl) {
    return (
      <AlertBlock
        title={messages.errorLoadingOrderPdf}
        description={messages.errorLoadingOrderDetails}
        alt={messages.orderNotFoundAlt}
      />
    );
  }

  return (
    <>
      <MDBox display="flex" justifyContent="flex-end" mb={2}>
        <MDButton variant="gradient" color="dark" onClick={printPdf}>
          {messages.printButtonLabel}
        </MDButton>
      </MDBox>
      <iframe
        ref={iframeRef}
        title={messages.orderPrintTitle}
        src={pdfUrl}
        onLoad={handleIframeLoad}
        style={{ width: "100%", height: "80vh", border: "none", background: "#fff" }}
      />
    </>
  );
}

export default OrderPdfPrint;
